import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Shield, Clock, MapPin, Activity, Ticket,
  CheckCircle, AlertCircle, LogIn, Globe, Loader2,
  Pencil, KeyRound, XCircle, BarChart3, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { cn } from "../../lib/utils";
import api from "../../lib/api";

const StaffProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => { fetchProfile(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    try {
      const res = await api.get(`/admin/platform-staff/${userId}/profile`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load staff profile");
      console.error(e);
    } finally { setLoading(false); }
  };

  const handleDeactivate = async () => {
    if (!window.confirm(`Deactivate ${data?.user?.full_name}? They will lose platform access.`)) return;
    try {
      await api.put(`/users/${userId}/status`, { status: "inactive" });
      toast.success("Staff member deactivated");
      fetchProfile();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) return (
    <AdminLayout title="Staff Profile" variant="super_admin">
      <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  if (!data) return (
    <AdminLayout title="Staff Profile" variant="super_admin">
      <div className="text-center py-20 text-muted-foreground">Staff member not found</div>
    </AdminLayout>
  );

  const { user, stats, top_actions, monthly_activity, recent_audit, ip_addresses } = data;
  const initials = (user.full_name || "?").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  const TABS = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "audit", label: "Activity Log", icon: FileText },
    { id: "security", label: "Security", icon: Shield },
  ];

  return (
    <AdminLayout title="Staff Profile" variant="super_admin">
      <div className="space-y-6" data-testid="staff-profile-page">
        {/* Back Button */}
        <Button variant="ghost" size="sm" className="h-8 gap-2 text-muted-foreground" onClick={() => navigate("/admin/platform-staff")} data-testid="back-to-staff">
          <ArrowLeft className="w-4 h-4" /> Platform Staff
        </Button>

        {/* Profile Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <Avatar className="h-20 w-20">
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>{user.full_name}</h2>
                  {user.is_active && user.status !== "inactive" ? (
                    <Badge className="bg-green-50 text-green-700 border-green-200" variant="outline"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>
                  ) : (
                    <Badge className="bg-red-50 text-red-700 border-red-200" variant="outline"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>
                  )}
                </div>
                <p className="text-muted-foreground">{user.email}</p>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <Badge className="bg-rose-50 text-rose-700 border-rose-200" variant="outline"><Shield className="w-3 h-3 mr-1" />{user.platform_role}</Badge>
                  {user.department && <Badge variant="outline">{user.department}</Badge>}
                  {user.job_title && <span className="text-sm text-muted-foreground">{user.job_title}</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => toast.info("Password reset sent (simulated)")} data-testid="reset-password-btn">
                  <KeyRound className="w-3 h-3" /> Reset Password
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-destructive border-red-200 hover:bg-red-50" onClick={handleDeactivate} data-testid="deactivate-staff-btn">
                  <XCircle className="w-3 h-3" /> Deactivate
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: "Total Actions", value: stats.total_actions, icon: Activity, color: "text-blue-600 bg-blue-50" },
            { label: "Tickets", value: stats.total_tickets, icon: Ticket, color: "text-amber-600 bg-amber-50" },
            { label: "Resolved", value: stats.resolved_tickets, icon: CheckCircle, color: "text-green-600 bg-green-50" },
            { label: "Open", value: stats.open_tickets, icon: AlertCircle, color: "text-red-600 bg-red-50" },
            { label: "Logins", value: stats.login_count, icon: LogIn, color: "text-indigo-600 bg-indigo-50" },
            { label: "Locations", value: stats.unique_ips, icon: Globe, color: "text-violet-600 bg-violet-50" },
            { label: "Avg Response", value: stats.avg_response_time, icon: Clock, color: "text-teal-600 bg-teal-50" },
          ].map(kpi => (
            <Card key={kpi.label}><CardContent className="p-3 text-center">
              <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2", kpi.color)}>
                <kpi.icon className="w-4 h-4" />
              </div>
              <p className="text-xl font-bold">{kpi.value}</p>
              <p className="text-[10px] text-muted-foreground">{kpi.label}</p>
            </CardContent></Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-lg w-fit">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className={cn("px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2",
                activeTab === tab.id ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Actions */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Top Actions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {top_actions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No actions recorded</p>
                ) : top_actions.map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-muted-foreground w-6">{i + 1}.</span>
                      <span className="text-sm font-medium">{a.action.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
                    </div>
                    <Badge variant="outline" className="text-xs">{a.count}x</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Monthly Activity */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly Activity</CardTitle></CardHeader>
              <CardContent>
                {Object.keys(monthly_activity).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No activity data</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(monthly_activity).map(([month, count]) => {
                      const max = Math.max(...Object.values(monthly_activity));
                      const pct = max > 0 ? (count / max) * 100 : 0;
                      return (
                        <div key={month} className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground w-16 shrink-0">{month}</span>
                          <div className="flex-1 h-6 bg-muted/30 rounded-full overflow-hidden">
                            <div className="h-full bg-primary/70 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-medium w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Staff Details */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Details</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {[
                  { label: "Email", value: user.email },
                  { label: "Role", value: user.platform_role },
                  { label: "Department", value: user.department || "—" },
                  { label: "Job Title", value: user.job_title || "—" },
                  { label: "Joined", value: user.created_at ? new Date(user.created_at).toLocaleDateString() : "—" },
                  { label: "Last Login", value: user.last_login ? new Date(user.last_login).toLocaleString() : "Never" },
                ].map(d => (
                  <div key={d.label} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <span className="text-sm text-muted-foreground">{d.label}</span>
                    <span className="text-sm font-medium">{d.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Known IPs */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Login Locations (IPs)</CardTitle></CardHeader>
              <CardContent>
                {ip_addresses.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No login data</p>
                ) : (
                  <div className="space-y-2">
                    {ip_addresses.map((ip, i) => (
                      <div key={i} className="flex items-center gap-3 py-1.5 border-b last:border-0">
                        <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-mono">{ip}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Activity Log Tab */}
        {activeTab === "audit" && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Activity Audit Log ({recent_audit.length} entries)</CardTitle></CardHeader>
            <CardContent className="p-0">
              {recent_audit.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No audit entries</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b bg-muted/30">
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Timestamp</th>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Action</th>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Details</th>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Resource</th>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">IP Address</th>
                    </tr></thead>
                    <tbody>
                      {recent_audit.map((log, i) => (
                        <tr key={i} className="border-b hover:bg-muted/10">
                          <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}</td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-[10px] font-mono">{log.action}</Badge>
                          </td>
                          <td className="p-3 text-xs max-w-[300px] truncate">{log.details || "—"}</td>
                          <td className="p-3 text-xs">{log.resource_type || "—"}</td>
                          <td className="p-3 text-xs font-mono text-muted-foreground">{log.ip_address || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Security Tab */}
        {activeTab === "security" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Login History</CardTitle></CardHeader>
              <CardContent>
                {recent_audit.filter(l => l.action?.toLowerCase().includes("login")).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No login events recorded</p>
                ) : (
                  <div className="space-y-2">
                    {recent_audit.filter(l => l.action?.toLowerCase().includes("login")).slice(0, 20).map((l, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-3">
                          <LogIn className="w-4 h-4 text-green-500" />
                          <div>
                            <p className="text-xs font-medium">{l.details || "Login"}</p>
                            <p className="text-[10px] text-muted-foreground">{l.ip_address}</p>
                          </div>
                        </div>
                        <span className="text-[10px] text-muted-foreground">{l.timestamp ? new Date(l.timestamp).toLocaleString() : "—"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Known IP Addresses</CardTitle></CardHeader>
              <CardContent>
                {ip_addresses.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No IPs recorded</p>
                ) : (
                  <div className="space-y-2">
                    {ip_addresses.map((ip, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-3">
                          <Globe className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-mono">{ip}</span>
                        </div>
                        <Badge variant="outline" className="text-[10px]">Trusted</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default StaffProfilePage;
